import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { nativeShare, CANONICAL_ORIGIN } from '../lib/shareUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  ArrowLeft, Star, Lock, Users, Globe, Share2, Trash2, 
  GripVertical, X, MapPin, ChevronRight, Copy, Check, User
} from 'lucide-react';

function ListDetail() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lists, bars, getBarStats, removeBarFromList, friends, friendLists } = useApp();
  const [copied, setCopied] = useState(false);
  const [fetchedList, setFetchedList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  // First, check if list is in user's own lists
  const ownList = useMemo(() => lists.find(l => l.id === listId), [lists, listId]);
  
  // Then check if it's in friend's lists
  const friendList = useMemo(() => friendLists?.find(l => l.id === listId), [friendLists, listId]);
  
  // If not found in either, fetch from database
  useEffect(() => {
    const fetchList = async () => {
      if (ownList || friendList || fetchedList) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('lists')
          .select(`
            *,
            list_bars(id, bar_id, position, added_at),
            profiles:user_id (id, username, display_name, avatar_url)
          `)
          .eq('id', listId)
          .single();

        if (error) throw error;
        
        if (!data) {
          setAccessDenied(true);
          return;
        }

        // Check privacy
        const isOwner = user?.id === data.user_id;
        const isFriend = friends.includes(data.user_id);
        
        if (data.privacy === 'private' && !isOwner) {
          setAccessDenied(true);
          return;
        }
        
        if (data.privacy === 'friends' && !isOwner && !isFriend) {
          setAccessDenied(true);
          return;
        }
        
        setFetchedList(data);
      } catch (err) {
        console.error('Error fetching list:', err);
        setAccessDenied(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchList();
  }, [listId, ownList, friendList, fetchedList, user, friends]);
  
  // Use whichever list we found
  const list = ownList || friendList || fetchedList;
  
  // Check if current user owns this list
  const isOwner = list && user?.id === list.user_id;
  const listOwner = list?.profiles;

  const listBars = useMemo(() => {
    if (!list?.list_bars) return [];
    return list.list_bars
      .sort((a, b) => a.position - b.position)
      .map(lb => {
        const bar = bars.find(b => b.id === lb.bar_id);
        if (!bar) return null;
        return {
          ...bar,
          listBarId: lb.id,
          position: lb.position,
          addedAt: lb.added_at
        };
      })
      .filter(Boolean);
  }, [list, bars]);

  const handleRemoveBar = async (barId) => {
    if (!confirm('Remove this bar from the list?')) return;
    try {
      await removeBarFromList(list.id, barId);
    } catch (err) {
      console.error('Error removing bar:', err);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${CANONICAL_ORIGIN}/lists/${listId}`;

    const shared = await nativeShare({
      title: list.title,
      text: list.description || `Check out this dive bar list: ${list.title}`,
      url: shareUrl
    });
    if (!shared) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getPrivacyInfo = (privacy) => {
    switch (privacy) {
      case 'public': 
        return { icon: Globe, label: 'Public', color: 'text-green-400' };
      case 'friends': 
        return { icon: Users, label: 'Friends only', color: 'text-blue-400' };
      default: 
        return { icon: Lock, label: 'Private', color: 'text-gray-400' };
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (accessDenied || !list) {
    return (
      <div className="p-4 text-center py-12">
        <Lock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 mb-2">
          {accessDenied ? "You don't have access to this list" : "List not found"}
        </p>
        <p className="text-gray-500 text-sm mb-4">
          This list may be private or only visible to friends
        </p>
        <button onClick={() => navigate('/lists')} className="btn-secondary mt-4">
          Back to Lists
        </button>
      </div>
    );
  }

  const privacyInfo = getPrivacyInfo(list.privacy);
  const PrivacyIcon = privacyInfo.icon;

  return (
    <div className="p-4 pb-24 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 bg-dive-dark rounded-lg text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {list.is_system && (
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            )}
            <h1 className="text-2xl font-display text-white">{list.title}</h1>
          </div>
          {list.description && (
            <p className="text-gray-400 text-sm mt-1">{list.description}</p>
          )}
          {/* Show owner if not own list */}
          {!isOwner && listOwner && (
            <Link 
              to={`/social/${list.user_id}`}
              className="flex items-center gap-2 mt-2 text-gray-400 hover:text-white transition-colors"
            >
              {listOwner.avatar_url ? (
                <img
                  src={listOwner.avatar_url}
                  alt={listOwner.display_name || listOwner.username}
                  className="w-5 h-5 rounded-full object-cover"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-dive-gray flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-500" />
                </div>
              )}
              <span className="text-sm">
                by {listOwner.display_name || listOwner.username}
              </span>
            </Link>
          )}
        </div>
      </div>

      {/* Stats & Actions */}
      <div className="flex items-center justify-between mb-6 p-4 bg-dive-dark rounded-xl border border-dive-gray">
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-1 ${privacyInfo.color}`}>
            <PrivacyIcon className="w-4 h-4" />
            <span className="text-sm">{privacyInfo.label}</span>
          </div>
          <div className="text-gray-400 text-sm">
            {listBars.length} {listBars.length === 1 ? 'bar' : 'bars'}
          </div>
        </div>
        
        {list.privacy !== 'private' && (
          <button
            onClick={handleShare}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400">Copied!</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                <span className="text-sm">Share</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Bar List */}
      {listBars.length === 0 ? (
        <div className="text-center py-12 bg-dive-dark rounded-xl border border-dive-gray">
          <img src="/logo-icon.png" alt="" className="w-12 h-12 object-contain mx-auto mb-4 opacity-50" />
          <p className="text-gray-400 mb-2">No bars in this list yet</p>
          <p className="text-gray-500 text-sm mb-4">
            Add bars from their detail pages
          </p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">
            Browse Bars
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {listBars.map((bar, index) => {
            const stats = getBarStats(bar.id);
            
            return (
              <div
                key={bar.id}
                className="bg-dive-dark rounded-xl border border-dive-gray p-4 flex items-center gap-4"
              >
                <div className="text-gray-500 font-mono text-sm w-6 text-center">
                  {index + 1}
                </div>
                
                <Link to={`/bar/${bar.id}`} className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold truncate">{bar.name}</h3>
                  <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {bar.neighborhood}
                    </span>
                    {stats.totalRatings > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        {stats.overall.toFixed(1)}
                      </span>
                    )}
                  </div>
                </Link>

                <Link
                  to={`/bar/${bar.id}`}
                  className="p-2 text-gray-400 hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </Link>

                {isOwner && !list.is_system && (
                  <button
                    onClick={() => handleRemoveBar(bar.id)}
                    className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ListDetail;

